import differenceInYears from 'date-fns/differenceInYears';
import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Button, Stack } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { patientFragment } from '@eluve/frontend-appointment-hooks';
import { useMedplum } from '@eluve/medplum-client';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { TabLink, TabNav, useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';
import { getVendorDataMapper } from '@eluve/vendor';

const baseTabs: TabLink[] = [
  {
    name: 'Appointments',
    link: 'appointments',
  },
  {
    name: 'Imported Notes',
    link: 'imported-notes',
  },
];

export interface PatientDetailsPageProps {}

export const PatientDetailsPage: React.FC<PatientDetailsPageProps> = () => {
  const { patientId } = useParams() as {
    patientId: string;
  };

  const patientDetails = useCompleteFragment({
    fragment: patientFragment,
    key: {
      id: patientId,
    },
  });

  const isUserInsightsChatEnabled =
    useIsPostHogFeatureFlagEnabled('INSIGHTS_CHAT');
  const isTenantInsightsChatEnabled = useIsFeatureFlagEnabled('INSIGHTS_CHAT');
  const isInsightsChatEnabled =
    isUserInsightsChatEnabled || isTenantInsightsChatEnabled;

  const { enabled: isMedplumEnabled } = useMedplum();
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');

  const tabLinks = useMemo(() => {
    const tabs = isInsightsChatEnabled
      ? [{ name: 'Artifacts', link: 'artifacts' }, ...baseTabs]
      : [...baseTabs];

    if (isMedplumEnabled && isCarePlansEnabled) {
      tabs.push({
        name: 'Care Plans',
        link: 'care-plans',
        matchSubpaths: true,
      });
    }

    if (isMedplumEnabled) {
      tabs.push({ name: 'Medical Profile', link: 'medical-profile' });
    }

    return tabs;
  }, [isInsightsChatEnabled, isMedplumEnabled, isCarePlansEnabled]);

  const name = formatHumanName(
    patientDetails?.firstName,
    patientDetails?.lastName,
  );
  const initials = `${patientDetails?.firstName?.charAt(0) ?? ''}${patientDetails?.lastName?.charAt(0) ?? ''}`;
  const dob = patientDetails.dateOfBirth
    ? formatDate.toDisplay(patientDetails.dateOfBirth)
    : null;
  const age = patientDetails?.dateOfBirth
    ? differenceInYears(new Date(), new Date(patientDetails.dateOfBirth))
    : null;

  const externalPatientInfos = patientDetails.external_patients_info ?? [];

  return (
    <Stack gap={8} className="py-3 md:p-8">
      {/* Patient Details */}
      <Stack gap={6} direction="horizontal">
        <div
          className={`flex aspect-square size-16 items-center justify-center rounded-full bg-text-primary leading-none text-white privacy-text md:size-24 ${'text-5xl font-medium text-text-primary'}`}
        >
          {initials}
        </div>

        <Stack gap={2}>
          <Stack gap={2}>
            <p
              className={`privacy-text ${'text-2xl font-semibold text-text-primary'}`}
            >
              {name}
            </p>
            {externalPatientInfos
              .filter((info) => info.external_ehr?.vendor)
              .map((info) => (
                <Stack key={info.id} gap={2} direction="horizontal">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(info.externalPatientId);
                    }}
                  >
                    {'Copy External ID'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const vendorProvider = getVendorDataMapper(
                        info.external_ehr.vendor,
                      );
                      const url = vendorProvider.getPatientEhrUrl({
                        domain: info.external_ehr.domain,
                        externalPatientId: info.externalPatientId,
                      });
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >{`View on ${info.external_ehr.vendor}`}</Button>
                </Stack>
              ))}
          </Stack>

          <Stack>
            {dob && (
              <p
                className={`privacy-text ${'text-sm font-medium text-text-primary'}`}
              >
                Date of Birth: <span className="font-bold">{dob}</span>
              </p>
            )}
            {age && (
              <p
                className={`privacy-text ${'text-sm font-medium text-text-primary'}`}
              >
                Age: <span className="font-bold">{age} years old</span>
              </p>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack gap={2}>
        {/* Tab View */}
        <TabNav tabs={tabLinks} />
        <Outlet />
      </Stack>
    </Stack>
  );
};
